import React from "react";
import { FiArrowDown, FiArrowLeft, FiArrowRight, FiArrowUp, FiSliders, FiTarget } from "react-icons/fi";
import type { AlertImageUploadField, FlowEditorNode, WidgetAnchor } from "./types";
import { getNodeAnchor } from "./flowUtils";
import { formatCurrency } from "./widgetUtils";
import { ColorInput, SelectInput, TextInput, ToggleRow } from "./FormControls";
import { AlertNodeSettings } from "./AlertNodeSettings";
type ImageUploadState = {
  nodeId: string;
  field: AlertImageUploadField;
  progress: number;
  error: string;
} | null;
type FlowNodeSettingsPanelProps = {
  node: FlowEditorNode;
  imageUploadState: ImageUploadState;
  applyAlertLayoutPreset: (nodeId: string, preset: any) => void;
  clearImageUploadState: (nodeId: string, field: AlertImageUploadField) => void;
  setNodeAnchor: (nodeId: string, anchor: WidgetAnchor) => void;
  updateNodeData: (nodeId: string, patch: Partial<FlowEditorNode["data"]>) => void;
  updateNodeSetting: (nodeId: string, key: string, value: any) => void;
  uploadNodeAlertImage: (node: FlowEditorNode, file: File, field: AlertImageUploadField) => void;
};
export function FlowNodeSettingsPanel({
  node,
  imageUploadState,
  applyAlertLayoutPreset,
  clearImageUploadState,
  setNodeAnchor,
  updateNodeData,
  updateNodeSetting,
  uploadNodeAlertImage,
}: FlowNodeSettingsPanelProps) {
const renderNodeSettings = (node: FlowEditorNode) => {
  const kind = node.data.kind || "text";
  const settings = node.data.settings || {};
  if (kind === "alert") {
    return (
      <AlertNodeSettings
        node={node}
        imageUploadState={imageUploadState}
        applyAlertLayoutPreset={applyAlertLayoutPreset}
        clearImageUploadState={clearImageUploadState}
        updateNodeData={updateNodeData}
        updateNodeSetting={updateNodeSetting}
        uploadNodeAlertImage={uploadNodeAlertImage}
      />
    );
  }
  if (kind === "goal") {
    return (
      <>
        <TextInput
          label="Title"
          value={String(settings.title || node.data.label || "")}
          onChange={(value) => {
            updateNodeSetting(node.id, "title", value);
            updateNodeData(node.id, { label: value });
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Current"
            type="number"
            value={String(settings.current || 0)}
            onChange={(value) =>
              updateNodeSetting(node.id, "current", Number(value || 0))
            }
          />
          <TextInput
            label="Target"
            type="number"
            value={String(settings.target || 0)}
            onChange={(value) =>
              updateNodeSetting(node.id, "target", Number(value || 0))
            }
          />
        </div>
        <ToggleRow
          label="Show percent"
          description="Goal node"
          checked={Boolean(settings.showPercent)}
          onChange={(checked) =>
            updateNodeSetting(node.id, "showPercent", checked)
          }
        />
      </>
    );
  }
  if (kind === "leaderboard") {
    return (
      <>
        <TextInput
          label="Title"
          value={String(settings.title || node.data.label || "")}
          onChange={(value) => {
            updateNodeSetting(node.id, "title", value);
            updateNodeData(node.id, { label: value });
          }}
        />
        <SelectInput
          label="Period"
          value={String(settings.period || "today")}
          options={[
            { value: "today", label: "Today" },
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
          ]}
          onChange={(value) => updateNodeSetting(node.id, "period", value)}
        />
        <TextInput
          label="Limit"
          type="number"
          value={String(settings.limit || 5)}
          onChange={(value) =>
            updateNodeSetting(node.id, "limit", Number(value || 0))
          }
        />
        <ToggleRow
          label="Show amount"
          description="List node"
          checked={settings.showAmounts !== false}
          onChange={(checked) =>
            updateNodeSetting(node.id, "showAmounts", checked)
          }
        />
      </>
    );
  }
  if (kind === "timer") {
    return (
      <>
        <TextInput
          label="Label"
          value={String(settings.label || node.data.text || "")}
          onChange={(value) => {
            updateNodeSetting(node.id, "label", value);
            updateNodeData(node.id, { text: value });
          }}
        />
        <TextInput
          label="Status"
          value={String(settings.status || "")}
          onChange={(value) => updateNodeSetting(node.id, "status", value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Minutes"
            type="number"
            value={String(settings.durationMinutes || 0)}
            onChange={(value) =>
              updateNodeSetting(
                node.id,
                "durationMinutes",
                Number(value || 0),
              )
            }
          />
          <TextInput
            label="Break"
            type="number"
            value={String(settings.breakMinutes || 0)}
            onChange={(value) =>
              updateNodeSetting(node.id, "breakMinutes", Number(value || 0))
            }
          />
        </div>
      </>
    );
  }
  if (kind === "chat") {
    return (
      <>
        <TextInput
          label="Title"
          value={String(settings.title || node.data.label || "")}
          onChange={(value) => {
            updateNodeSetting(node.id, "title", value);
            updateNodeData(node.id, { label: value });
          }}
        />
        <SelectInput
          label="Theme"
          value={String(settings.theme || "light")}
          options={[
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
            { value: "glass", label: "Glass" },
          ]}
          onChange={(value) => updateNodeSetting(node.id, "theme", value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Font"
            type="number"
            value={String(settings.fontSize || 14)}
            onChange={(value) =>
              updateNodeSetting(node.id, "fontSize", Number(value || 0))
            }
          />
          <TextInput
            label="Height"
            type="number"
            value={String(settings.height || 620)}
            onChange={(value) =>
              updateNodeSetting(node.id, "height", Number(value || 0))
            }
          />
        </div>
        <ToggleRow
          label="Avatar"
          description="Chat node"
          checked={settings.showAvatar !== false}
          onChange={(checked) =>
            updateNodeSetting(node.id, "showAvatar", checked)
          }
        />
      </>
    );
  }
  if (kind === "image") {
    return (
      <TextInput
        label="Image URL"
        value={String(settings.imageUrl || node.data.imageUrl || "")}
        onChange={(value) => {
          updateNodeSetting(node.id, "imageUrl", value);
          updateNodeData(node.id, { imageUrl: value });
        }}
      />
    );
  }
  if (kind === "amount") {
    return (
      <TextInput
        label="Amount"
        type="number"
        value={String(settings.amount || 0)}
        onChange={(value) => {
          updateNodeSetting(node.id, "amount", Number(value || 0));
          updateNodeData(node.id, { text: formatCurrency(value || 0) });
        }}
      />
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <TextInput
        label="Font"
        type="number"
        value={String(settings.fontSize || 18)}
        onChange={(value) =>
          updateNodeSetting(node.id, "fontSize", Number(value || 0))
        }
      />
      <SelectInput
        label="Weight"
        value={String(settings.fontWeight || "bold")}
        options={[
          { value: "normal", label: "Normal" },
          { value: "bold", label: "Bold" },
        ]}
        onChange={(value) => updateNodeSetting(node.id, "fontWeight", value)}
      />
    </div>
  );
};
const renderNodeSettingsForm = (node: FlowEditorNode) => {
  const positionPresets: Array<{
    value: WidgetAnchor;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
    {
      value: "free",
      label: "Free",
      description: "Drag anywhere on canvas",
      icon: FiSliders,
    },
    {
      value: "center",
      label: "Center",
      description: "Show alert in the middle",
      icon: FiTarget,
    },
    {
      value: "top",
      label: "Top",
      description: "Pin alert to top center",
      icon: FiArrowUp,
    },
    {
      value: "bottom",
      label: "Bottom",
      description: "Pin alert to bottom center",
      icon: FiArrowDown,
    },
    {
      value: "left",
      label: "Left",
      description: "Pin alert to left center",
      icon: FiArrowLeft,
    },
    {
      value: "right",
      label: "Right",
      description: "Pin alert to right center",
      icon: FiArrowRight,
    },
  ];
  const renderPositionPresetOptions = () => (
    <div className="grid gap-2">
      <div className="text-xs font-black text-[#344054]">Position preset</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {positionPresets.map((option) => {
          const Icon = option.icon;
          const isActive = getNodeAnchor(node) === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`flex min-h-[58px] items-center gap-3 rounded-lg border px-3 text-left transition ${
                isActive
                  ? "border-[#168feb] bg-[#eef7ff] text-[#168feb] shadow-[0_0_0_2px_rgba(22,143,235,0.12)]"
                  : "border-[#dbe4ef] bg-white text-[#475467] hover:border-[#98bfe4] hover:text-[#168feb]"
              }`}
              onClick={() => setNodeAnchor(node.id, option.value)}
            >
              <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-[#f4f7fb]">
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-black">
                  {option.label}
                </span>
                <span className="block truncate text-xs font-medium text-[#667085]">
                  {option.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
  if ((node.data.kind || "text") === "alert") {
    return (
      <div className="grid gap-3">
        {renderPositionPresetOptions()}
        <div className="h-px bg-[#dbe4ef]" />
        {renderNodeSettings(node)}
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label="Label"
          value={node.data.label || ""}
          onChange={(value) => updateNodeData(node.id, { label: value })}
        />
        <TextInput
          label="Text"
          value={node.data.text || ""}
          onChange={(value) => {
            updateNodeData(node.id, { text: value });
            if ((node.data.kind || "text") === "alert") {
              updateNodeSetting(node.id, "messageTemplate", value);
            }
          }}
        />
        <ColorInput
          label="Color"
          value={node.data.color || "#168FEB"}
          onChange={(value) => updateNodeData(node.id, { color: value })}
        />
      </div>
      {renderPositionPresetOptions()}
      <div className="h-px bg-[#dbe4ef]" />
      {renderNodeSettings(node)}
    </div>
  );
};
  return renderNodeSettingsForm(node);
}
